import React, {useCallback, useEffect, useRef, useState} from "react";
import {inject, observer} from "mobx-react";
import {QRCodeSVG} from "qrcode.react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
//local
import {Preloader} from "components/Preloader";
import {getBankIDStatus} from "helpers/helper";
import {showSuccess} from "helpers/notifications.helper";
import {SAVE_SEARCH_REGIONS} from "constants/routes.const";

export const ModalIdentifyOwner = inject("store")(
  observer(({store: {auth, organization}}) => {
    const {activeOrganizationId, qrCodeData, autoStartToken} = auth;
    const {t} = useTranslation();
    let navigate = useNavigate();
    const intervalRef = useRef(null);
    const timeoutRef = useRef(null);
    const userMessage = auth.bankIdVerifyData?.userMessage;
    const COUNTDOWN_START = 300; // 5 minutes in seconds
    const [timeLeft, setTimeLeft] = useState(COUNTDOWN_START);

    useEffect(() => {
      auth.getBankIdData();
      intervalRef.current = setInterval(() => {
        auth.getBankIdQrCode();
      }, 1000);

      const countdownInterval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);

            auth.cancelAuthBankId();
            auth.setBankIdVerifyData({});
            auth.updateAuthModalName(null);

            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, [auth]);

    useEffect(() => {
      if (auth.bankIdVerifyData?.hintCode === "startFailed") {
        auth.getBankIdData();

        if (intervalRef.current) clearInterval(intervalRef.current);

        intervalRef.current = setInterval(() => {
          auth.getBankIdQrCode();
        }, 1000);
      }
    }, [auth, auth.bankIdVerifyData?.hintCode]);

    const checkBankIdStatus = useCallback(() => {
      switch (auth.bankIdVerifyData?.status) {
        case "failed":
          if (auth.bankIdVerifyData?.hintCode === "startFailed") {
            return getBankIDStatus(userMessage);
          }

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          timeoutRef.current = setTimeout(() => {
            auth.updateAuthModalName(null);
            auth.setBankIdVerifyData({});
          }, 3000);

          return getBankIDStatus(userMessage);
        case "complete":
          showSuccess(t("VERIFICATION_COMPLETED"));

          auth.updateAuthModalName(null);
          organization.setOrganizationOnboardingStatus(activeOrganizationId, {
            bankId: true,
          });

          auth.checkToken().then(() => navigate(SAVE_SEARCH_REGIONS));

          return;
        default:
          return getBankIDStatus(userMessage);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth, t, userMessage, organization, activeOrganizationId, navigate]);

    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    return (
      <div className="modal-auth modal-identify_wrapper">
        <div className="modal-identify_image">
          {qrCodeData ? (
            <div>
              <button
                style={{border: "none", background: "none"}}
                onClick={() =>
                  document
                    .querySelector(".modal-ent__wrapper")
                    .classList.add("fullscreen")
                }
              >
                <QRCodeSVG value={qrCodeData} size={180} />
              </button>
              <div className="modal-identify_timer">
                {t("TIME_LEFT")}: {formatTime(timeLeft)}
              </div>
            </div>
          ) : (
            <Preloader addClass="preloader500" />
          )}
        </div>

        <p className={`modal-identify_text ${qrCodeData ? "mb-2" : "mb-12"}`}>
          {checkBankIdStatus()}
        </p>

        {qrCodeData && (
          <a
            href={`bankid:///?autostarttoken=${autoStartToken}&redirect=${window.location.origin}/onboarding`}
            className="modal-identify_link"
          >
            {t("OPEN_BANK_ID_LINK")}
          </a>
        )}
      </div>
    );
  })
);
